import { useContext, useEffect, useMemo, useState, useRef } from 'react';
import { IWatrReimbursement, IWatrRequest } from '@nkr/models/index';
import { PanelGroup } from 'react-bootstrap';
import { FileDocumentType } from '@nkr/enums/document-type';
import { WageAndTravelType } from '@nkr/enums/index';
import { toast } from 'react-toastify';
import { DonorShieldContext } from '../context';
import { buildWageVerificationSupplementalUploads } from '@nkr/views/donors/watr/utils/watr-wage-verification-uploads';
import { IWatrSupplementalUploadedDoc } from '@nkr/views/donors/watr/utils/watr-supplemental-uploaded-doc';
import ReimbursementLog from '../reimbursement-log';
import AuditLog from '../audit-log';
import Affirmations from './affirmations';
import Wages from './wages';
import DonorInformation from './donor-information';
import { IWatrLineItem } from '@nkr/models/watr-request-travels';
import { openWatrReceiptInSidebar } from '../../utils/open-watr-receipt-in-sidebar';
import TravelExpenseReimbursementPanel from './travel-expense-reimbursement-panel';
import UploadedDocumentsPanel from './uploaded-documents-panel';
import { isIntlArVisitReimbursementEntry } from '../advanced-reimbursement-status-buckets';
import { WATR_OPEN_AUDIT_LOG_DECLINED, WatrAuditLogFocusDetail } from '../watr-audit-log-focus';

interface IProps {
    data: IWatrRequest,
    isAudit: boolean,
    reimbursements: IWatrReimbursement[],
    openReconcileReimbursementEntry?: boolean,
    paymentWorkflow?: 'wages' | 'expenses',
    reimbursementType?: number,
    allowEditWeekOtherAmount?: boolean,
    onWeekOtherAmountChange?: (weekRequested: number, otherAmount: number) => void,
    draftWeekOtherAmounts?: Record<number, number>,
    onSaveWeekOtherAmount?: (weekRequested: number, otherAmount: number) => void | Promise<void>,
    savingWeekRequested?: number | null,
    saveError?: string | null
}

const WatrInfoAccordions = (props: IProps) => {
    const [incomePercentCovered, setIncomePercentCovered] = useState(0);
    const [activeKey, setActiveKey] = useState(null);
    const [auditLogFocusDeclinedToken, setAuditLogFocusDeclinedToken] = useState(0);

    const context = useContext(DonorShieldContext);

    const accordionRef = useRef(null);

    const auditLogPanelKey = '5';

    useEffect(() => {
        setIncomePercentCovered(props.data.incomePercentCovered);

        if (props.isAudit && context.isNkrSuper) {
            setActiveKey(auditLogPanelKey);
        } else if (
            props.openReconcileReimbursementEntry
            || props.paymentWorkflow === 'wages'
            || props.paymentWorkflow === 'expenses'
            || props.reimbursementType == 3
            || props.reimbursementType == 2
        ) {
            setActiveKey('4');
        }
    }, [props.data, props.openReconcileReimbursementEntry, props.paymentWorkflow, props.reimbursementType, props.isAudit, context.isNkrSuper])

    useEffect(() => {
        const onOpenDeclined = (event: Event) => {
            const custom = event as CustomEvent<WatrAuditLogFocusDetail>;
            const token = custom?.detail?.token || Date.now();
            setActiveKey(auditLogPanelKey);
            setAuditLogFocusDeclinedToken(token);
            window.setTimeout(() => {
                const panel = document.querySelector('.watr-audit-log-panel');
                if (panel && typeof (panel as HTMLElement).scrollIntoView === 'function') {
                    (panel as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 50);
        };
        window.addEventListener(WATR_OPEN_AUDIT_LOG_DECLINED, onOpenDeclined as EventListener);
        return () => {
            window.removeEventListener(WATR_OPEN_AUDIT_LOG_DECLINED, onOpenDeclined as EventListener);
        };
    }, []);

    const handleViewFilesForTrip = async (trip: any, lineItems: IWatrLineItem[]) => {
        if (!lineItems || lineItems.length === 0) {
            return;
        }

        const itemsWithFiles = lineItems.filter(li => li.receiptFileId);
        if (itemsWithFiles.length === 0) {
            return;
        }

        const fileIds = [...new Set(itemsWithFiles.map(li => li.receiptFileId!).filter(Boolean))];
        const fileIdToLineItemsMap = new Map<number, any[]>();
        
        itemsWithFiles.forEach(li => {
            if (li.receiptFileId) {
                if (!fileIdToLineItemsMap.has(li.receiptFileId)) {
                    fileIdToLineItemsMap.set(li.receiptFileId, []);
                }
                fileIdToLineItemsMap.get(li.receiptFileId)!.push(li);
            }
        });

        try {
            const firstFileUrl = await context.fetchFileUrl(FileDocumentType.Receipts, fileIds[0]);
            context.onShowDataSideBar(
                FileDocumentType.Receipts,
                trip.id,
                firstFileUrl,
                fileIds.length,
                fileIds,
                0,
                fileIdToLineItemsMap
            );
        } catch (err) {
            console.error('Error viewing files:', err);
        }
    };

    const handleViewUploadedDocument = (fileId: number) =>
        openWatrReceiptInSidebar(context.downloadFile, context.onShowDataSideBar, fileId);

    const wageVerificationSupplementalDocs = useMemo(
        () => buildWageVerificationSupplementalUploads(props.data),
        [props.data]
    );

    const handleViewSupplementalDocument = async (doc: IWatrSupplementalUploadedDoc) => {
        try {
            const fileUrl = await context.fetchFileUrl(doc.docType, props.data.id);
            context.onShowDataSideBar(doc.docType, props.data.id, fileUrl);
        } catch (e: any) {
            const msg = e?.response?.data?.message || e?.message || 'Unable to view file.';
            toast.error(msg);
        }
    };

    const handlePanelSelect = (e) => {
        setActiveKey(e);
    }

    const hasPayStub = props.data.payStubFile;
    const hasTaxFile = props.data.taxDocumentFile;
    const hasSelfEmployed = props.data.selfEmployedAffirmationFile;
    const travelAffidavits = props.data.requestTravels.filter(x =>
        x.dependentCareFile != null && x.dependentCareFile != ''
        || x.dependentCompanionFile != null && x.dependentCompanionFile != ''
        || x.paymentAssistanceFile != null && x.paymentAssistanceFile != '');

    const isPaymentWorkflow = props.paymentWorkflow === 'wages' || props.paymentWorkflow === 'expenses';
    const travelReimburseSidebar = props.paymentWorkflow === 'expenses'
        || (!isPaymentWorkflow
            && (props.reimbursementType === WageAndTravelType.ReimburseTravelOnly
                || props.reimbursementType === WageAndTravelType.ReimburseBoth));
    const wageReimburseSidebar = props.paymentWorkflow === 'wages'
        || (!isPaymentWorkflow
            && (!travelReimburseSidebar || props.reimbursementType === WageAndTravelType.ReimburseBoth));
    const openIntlArVisitReimbursementEntry = travelReimburseSidebar
        && isIntlArVisitReimbursementEntry(props.data);

    return (
        <PanelGroup accordion id='wage-accordion' activeKey={activeKey} ref={accordionRef} onSelect={handlePanelSelect}>
            {context.isNkrSuper &&
                <AuditLog
                    data={props.data}
                    auditLogTypes={context.auditLogTypes}
                    logActionTypes={context.logActionTypes}
                    container={accordionRef.current}
                    downloadFile={context.downloadFile}
                    currentUser={context.currentUser}
                    focusDeclinedToken={auditLogFocusDeclinedToken}
                    onSaveLogEntry={context.handleSaveLogEntry}
                    onUpdateLogEntry={context.handleUpdateLogEntry}
                    onDeleteLogEntry={context.handleDeleteLogEntry}
                />
            }

            <DonorInformation
                data={props.data}
                donationTypes={context.donationTypes} />

            <Wages
                data={props.data}
                incomePercentCovered={incomePercentCovered}
                hasPayStub={hasPayStub}
                hasTaxFile={hasTaxFile}
                allowEditWeekOtherAmount={props.allowEditWeekOtherAmount}
                onWeekOtherAmountChange={props.onWeekOtherAmountChange}
                draftWeekOtherAmounts={props.draftWeekOtherAmounts}
                onSaveWeekOtherAmount={props.onSaveWeekOtherAmount}
                savingWeekRequested={props.savingWeekRequested}
                saveError={props.saveError} />

            <TravelExpenseReimbursementPanel
                request={props.data}
                onViewFilesForTrip={handleViewFilesForTrip}
            />

            <UploadedDocumentsPanel
                fileUploadRecords={props.data.fileUploadRecords || []}
                // supplementalDocs={wageVerificationSupplementalDocs}
                onViewUploadedDocument={handleViewUploadedDocument}
                // onViewSupplementalDocument={handleViewSupplementalDocument}
            />

            {(travelAffidavits.length > 0 || hasSelfEmployed) && (
                <Affirmations
                    id={props.data.id}
                    firstName={props.data.firstName}
                    lastName={props.data.lastName}
                    hasSelfEmployed={hasSelfEmployed}
                    travelAffidavits={travelAffidavits} />
            )}

            <ReimbursementLog
                isNkrSuper={context.isNkrSuper}
                request={props.data}
                reimbursements={props.reimbursements}
                container={this}
                isMobile={false}
                usePanel={true}
                types={context.reimbursementTypes}
                isReimburseTravel={travelReimburseSidebar}
                isReimburseWage={wageReimburseSidebar}
                openAddReimbursementOnMount={isPaymentWorkflow}
                openReconcileReimbursementEntry={props.openReconcileReimbursementEntry}
                openIntlArVisitReimbursementEntry={openIntlArVisitReimbursementEntry}
                onSaveReimbursementEntry={context.onReimbursementEntry}
                onReimbursementUpdate={context.onReimbursementUpdate}
            />
        </PanelGroup>
    );
}

export default WatrInfoAccordions